import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import TGChibi from '../components/TGChibi';
import { EnvelopeIcon, ShieldCheckIcon, LockClosedIcon } from '@heroicons/react/24/outline';

export default function Login() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { requestMagicLink } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🔵 Login: Form submitted with email:', email);
    
    if (!termsAccepted) {
      setError('You must accept the Terms of Service and Privacy Policy to continue.');
      return;
    }
    
    setLoading(true);
    setError('');
    setMessage('');

    try {
      console.log('🔵 Login: Calling requestMagicLink...');
      const result = await requestMagicLink(email);
      console.log('🔵 Login: requestMagicLink result:', result);
      
      if (result.success) {
        console.log('✅ Login: Magic link sent successfully');
        setMessage('Magic link sent! Check your email to sign in.');
      } else {
        console.log('❌ Login: Magic link failed:', result.error);
        setError(result.error);
      }
    } catch (error) {
      console.error('💥 Login: Unexpected error in handleSubmit:', error);
      setError('An unexpected error occurred. Please try again.');
    }
    
    setLoading(false);
    console.log('🔵 Login: Form submission completed');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-950 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <Link to="/" className="inline-flex items-center space-x-3">
              <img 
                src="/TGlogo.png" 
                alt="TypoGuard Logo" 
                className="w-12 h-12 object-contain"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                TypoGuard
              </span>
            </Link>
            <TGChibi 
              size="md" 
              animation="none" 
              showSpeechBubble={true}
              speechBubbleText="Hello there! 👋"
            />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-3">
            Sign-In to TypoGuard
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Enter your email to receive a secure magic link
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white dark:bg-dark-900 shadow-2xl rounded-3xl p-8 border border-gray-200 dark:border-dark-800">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-12 pr-4 py-4 border border-gray-300 dark:border-dark-700 rounded-xl placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-dark-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 text-lg"
                  placeholder="Enter your email address"
                  disabled={loading}
                />
              </div>
            </div>

            {error && (
              <div className="rounded-xl bg-red-50 dark:bg-red-900/20 p-4 border border-red-200 dark:border-red-800">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {message && (
              <div className="rounded-xl bg-green-50 dark:bg-green-900/20 p-4 border border-green-200 dark:border-green-800">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800 dark:text-green-200">{message}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Terms and Privacy Policy Checkbox */}
            <div className="flex items-start mt-4">
              <div className="flex items-center h-5">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="terms" className="font-medium text-gray-700 dark:text-gray-300">
                  I accept the{' '}
                  <Link to="/legal/terms-of-service" target="_blank" className="text-primary-600 hover:text-primary-500 underline">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/legal/privacy-policy" target="_blank" className="text-primary-600 hover:text-primary-500 underline">
                    Privacy Policy
                  </Link>
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !email || !termsAccepted}
              className="w-full flex justify-center items-center py-4 px-6 border border-transparent text-lg font-semibold rounded-xl text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Sending Magic Link...</span>
                </>
              ) : (
                <>
                  <LockClosedIcon className="w-5 h-5 mr-2" />
                  Send Magic Link
                </>
              )}
            </button>
          </form>

          {/* Security Features */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-dark-700">
            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <ShieldCheckIcon className="w-4 h-4 mr-2 text-primary-500" />
                <span>Passwordless authentication</span>
              </div>
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <LockClosedIcon className="w-4 h-4 mr-2 text-primary-500" />
                <span>Secure magic link expires in 15 minutes</span>
              </div>
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <EnvelopeIcon className="w-4 h-4 mr-2 text-primary-500" />
                <span>No passwords to remember or store</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium transition-colors duration-200"
          >
            ← Back to home
          </Link>
          
          <div className="mt-6">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              New to TypoGuard? Simply enter your email above to create an account and get started.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
